import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  message,
  Typography,
  Card,
  Tag,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Expense, ExpenseFormData, ExpenseCategory } from '../../types/expense';
import { expenseService } from '../../services/expenseService';
import { supplierService } from '../../services/supplierService';
import { Supplier } from '../../services/supplierService';
import { DatePickerModal } from '../common/DatePickerModal';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface ExpenseDetailModalMobileProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  editingExpense?: Expense;
  isLoadingDetail?: boolean;
}

const ExpenseDetailModalMobile: React.FC<ExpenseDetailModalMobileProps> = ({
  visible,
  onCancel,
  onSuccess,
  editingExpense,
  isLoadingDetail = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [datePickerState, setDatePickerState] = useState<{
    isOpen: boolean;
    currentDate: Dayjs | null;
  }>({
    isOpen: false,
    currentDate: null
  });

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (visible) {
      if (editingExpense && !isLoadingDetail) {
        // Edit mode - populate form with existing data
        const formData = {
          title: editingExpense.title,
          description: editingExpense.description,
          category_id: editingExpense.category_id,
          amount: editingExpense.amount,
          expense_date: editingExpense.expense_date ? dayjs(editingExpense.expense_date) : null,
          payment_method: editingExpense.payment_method || 'cash',
          payment_status: editingExpense.payment_status || 'pending',
          supplier_id: editingExpense.supplier_id,
          invoice_number: editingExpense.invoice_number,
          notes: editingExpense.notes
        };
        setInitialValues(formData);
        setIsFormInitialized(true);
      } else if (!editingExpense && !isLoadingDetail) {
        // Create mode - reset form
        setInitialValues({
          payment_method: 'cash',
          payment_status: 'paid',
          amount: 0,
          expense_date: dayjs()
        });
        setIsFormInitialized(true);
      }
    } else {
      setInitialValues(null);
      setIsFormInitialized(false);
    }
  }, [visible, editingExpense, isLoadingDetail]);

  // Đảm bảo form được set values sau khi initialValues được set
  useEffect(() => {
    if (initialValues && isFormInitialized && visible) {
      setTimeout(() => {
        form.setFieldsValue(initialValues);
      }, 200);
    }
  }, [initialValues, isFormInitialized, visible, form]);

  // Load categories and suppliers when drawer opens
  useEffect(() => {
    if (visible) {
      loadCategories();
      loadSuppliers();
    }
  }, [visible]);

  const loadCategories = async () => {
    try {
      const data = await expenseService.getCategories();
      setCategories(data);
    } catch (error) {
      message.error('Không thể tải danh mục chi phí');
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      message.error('Không thể tải danh sách nhà cung cấp');
    }
  };

  const handleCancelInternal = () => {
    form.resetFields();
    setInitialValues(null);
    setIsFormInitialized(false);
    setDatePickerState({
      isOpen: false,
      currentDate: null
    });
    onCancel();
  };

  const openDatePicker = (currentDate?: Dayjs) => {
    setDatePickerState({
      isOpen: true,
      currentDate: currentDate || null
    });
  };

  const closeDatePicker = () => {
    setDatePickerState({
      isOpen: false,
      currentDate: null
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    form.setFieldValue('expense_date', date);
    closeDatePicker();
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData: ExpenseFormData = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        category_id: values.category_id,
        supplier_id: values.supplier_id || null,
        payment_status: values.payment_status || 'pending'
      };

      if (editingExpense) {
        await expenseService.updateExpense(editingExpense.id, formData);
        message.success('Cập nhật chi phí thành công');
      } else {
        // Đảm bảo payment_status được set khi tạo mới
        const createData = {
          ...formData,
          payment_status: formData.payment_status || 'pending'
        };
        await expenseService.createExpense(createData);
        message.success('Tạo chi phí thành công');
      }
      
      handleCancelInternal();
      
      // Gọi callback onSuccess nếu có
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      message.error('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={editingExpense ? 'Sửa chi phí' : 'Tạo chi phí mới'}
      open={visible}
      onClose={handleCancelInternal}
      placement="bottom"
      height="90vh"
      bodyStyle={{ padding: 12, background: '#f5f6fa' }}
      destroyOnClose
      className="expense-mobile-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        style={{ width: '100%' }}
      >
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thông tin cơ bản</Title>
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
          >
            <Input placeholder="Nhập tiêu đề chi phí" />
          </Form.Item>
          
          <Form.Item
            name="category_id"
            label="Danh mục"
            rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
          >
            <Select placeholder="Chọn danh mục">
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="amount"
            label="Số tiền"
            rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
          >
            <Input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ width: '100%' }}
              placeholder="0"
              min="0"
              step="1000"
            />
          </Form.Item>
          
          <Form.Item
            name="expense_date"
            label="Ngày chi phí"
            rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <Input 
                placeholder="Chọn ngày" 
                value={form.getFieldValue('expense_date') ? dayjs(form.getFieldValue('expense_date')).format('DD/MM/YYYY') : ''}
                readOnly
                style={{ flex: 1 }}
              />
              <Button 
                type="primary" 
                icon={<CalendarOutlined />} 
                onClick={() => openDatePicker(form.getFieldValue('expense_date'))}
              />
            </div>
          </Form.Item>
        </Card>

        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thông tin thanh toán</Title>
          
          <Form.Item
            name="payment_method"
            label="Phương thức thanh toán"
          >
            <Select>
              <Option value="cash">Tiền mặt</Option>
              <Option value="bank_transfer">Chuyển khoản</Option>
              <Option value="credit_card">Thẻ tín dụng</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="payment_status"
            label="Trạng thái thanh toán"
          >
            <Select>
              <Option value="pending">Chờ thanh toán</Option>
              <Option value="paid">Đã thanh toán</Option>
              <Option value="cancelled">Đã hủy</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="supplier_id"
            label="Nhà cung cấp"
          >
            <Select placeholder="Chọn nhà cung cấp" allowClear>
              {suppliers.map(supplier => (
                <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="invoice_number"
            label="Số hóa đơn"
          >
            <Input placeholder="Nhập số hóa đơn" />
          </Form.Item>
        </Card>

        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Mô tả và ghi chú</Title>
          <Form.Item
            name="description"
            label="Mô tả"
          >
            <TextArea rows={3} placeholder="Mô tả chi tiết chi phí" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Ghi chú"
          >
            <TextArea rows={2} placeholder="Ghi chú bổ sung" />
          </Form.Item>
        </Card>

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelInternal}>Hủy</Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            htmlType="submit" 
            loading={loading}
          >
            {editingExpense ? 'Cập nhật' : 'Tạo chi phí'}
          </Button>
        </Space>
      </Form>

      {/* DatePickerModal */}
      <DatePickerModal
        isOpen={datePickerState.isOpen}
        onClose={closeDatePicker}
        onSelect={handleDateSelect}
        selectedDate={datePickerState.currentDate || undefined}
        title="Chọn ngày chi phí"
      />
    </Drawer>
  );
};

export default ExpenseDetailModalMobile;
